import type { useDetailsSummaryADFQueryQuery$variables } from '../hooks/__generated__/useDetailsSummaryADFQueryQuery.graphql.ts';

import { DEFAULT_MAX_PAGE_SIZE, MAX_PDF_PAGE_SIZE } from './pprConsts.ts';

/**
 * Shape of the `macroParams` bag passed to the DetailsSummary macro. We
 * intentionally keep this loose (string-keyed, optional values) so the helper
 * can be called from:
 *  - the runtime renderer, which receives a fully typed
 *    `PagePropertiesReportMacroParams`,
 *  - the SSR preload paths, which read the bag straight from an ADF extension
 *    node's `attrs.parameters.macroParams`.
 */
export type DetailsSummaryMacroParamMap = Record<string, { value: string } | undefined> | undefined;

export type BuildDetailsSummaryVariablesInput = {
	/** Flattened macro params bag (e.g. `{ cql: { value: '...' } }`). */
	macroParams: DetailsSummaryMacroParamMap;
	/** The current page contentId; used when `macroParams._parentId` is absent. */
	contentId: string;
	/** The current space key. */
	spaceKey: string;
	/** The macro instance id; pass `''` when unknown. */
	macroId: string;
	/** Stateful page index; defaults to `0` (always `0` for SSR preloads). */
	pageIndex?: number;
	/** Whether the renderer is mounted in the PDF-export route. */
	isPDFExport?: boolean;
};

const isTrue = (params: DetailsSummaryMacroParamMap, key: string): boolean =>
	params?.[key]?.value === 'true';

/**
 * Resolve the effective `pageSize` query variable for the details-summary
 * macro.
 *
 * Rules (kept in one place so the runtime hook, the SSR preload-macros
 * handler, and the legacy SSR preload all behave identically):
 *
 * - When rendering inside a PDF export, always request the maximum page size
 *   (`MAX_PDF_PAGE_SIZE`) so the exported document contains every row.
 * - Otherwise, parse the user-supplied `pageSize` macro param as a base-10
 *   integer. Missing / unparseable values fall back to the default
 *   (`DEFAULT_MAX_PAGE_SIZE`).
 * - The parsed value is then clamped to `DEFAULT_MAX_PAGE_SIZE` so a macro
 *   author cannot accidentally request a larger page than the server is
 *   tuned for.
 */
const resolvePageSize = (
	macroParams: DetailsSummaryMacroParamMap,
	isPDFExport: boolean,
): number => {
	if (isPDFExport) {
		return MAX_PDF_PAGE_SIZE;
	}

	const rawValue = macroParams?.['pageSize']?.value;
	const parsed = parseInt(rawValue || `${DEFAULT_MAX_PAGE_SIZE}`, 10);

	if (Number.isNaN(parsed)) {
		return DEFAULT_MAX_PAGE_SIZE;
	}

	return Math.min(parsed, DEFAULT_MAX_PAGE_SIZE);
};

/**
 * Build the variables object for `useDetailsSummaryADFQueryQuery` from a
 * `macroParams` bag plus the surrounding context. This is the single source of
 * truth for that mapping to guarantee all call sites fire the same query for the
 * same logical input.
 */
export const buildDetailsSummaryVariables = ({
	macroParams,
	contentId,
	spaceKey,
	macroId,
	pageIndex = 0,
	isPDFExport = false,
}: BuildDetailsSummaryVariablesInput): useDetailsSummaryADFQueryQuery$variables => ({
	// `_parentId` is the source page where the macro was inserted; it is
	// present when nested inside of other macros and should be used instead of
	// the current page contentId.
	contentId: macroParams?.['_parentId']?.value || contentId,
	cql: macroParams?.['cql']?.value,
	detailsId: macroParams?.['id']?.value,
	headings: macroParams?.['headings']?.value,
	sortBy: macroParams?.['sortBy']?.value,
	spaceKey,
	countLikes: isTrue(macroParams, 'showLikesCount'),
	reverseSort: isTrue(macroParams, 'reverseSort'),
	countComments: isTrue(macroParams, 'showCommentsCount'),
	countUnresolvedComments: isTrue(macroParams, 'showUnresolvedCommentsCount'),
	showPageLabels: isTrue(macroParams, 'showPageLabels'),
	showLastModified: isTrue(macroParams, 'showLastModified'),
	showCreator: isTrue(macroParams, 'showCreator'),
	pageIndex,
	macroId,
	pageSize: resolvePageSize(macroParams, isPDFExport),
});
